import Collection from "@arcgis/core/core/Collection";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Layer from "@arcgis/core/layers/Layer";
import Query from "@arcgis/core/rest/support/Query.js";
import MapView from "@arcgis/core/views/MapView";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppConfig } from "../../AppConfig";
import { LayerFieldsIB } from "../../constants/LayerFields";
import { JaNei } from "../../constants/nfsDomains";
import { LeftPanelTabs } from "../../constants/Panels";
import { StoreState } from "../../store/rootReducer";
import { setCatalogLoading } from "../../store/searchSlice";
import { GetCodedValueAndAlias, getMapView } from "../../utils/arcgisUtils";
import Loader from "../genericComponents/Loader/Loader";
import "./Catalog.css";
import ResultsFilter from "./ResultsFilter";
import SearchItem from "./SearchItem";
import type Graphic from "@arcgis/core/Graphic";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface CatalogProps {
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
}

const Catalog = (props: CatalogProps) => {
    const { storeHighlightHandle, clearHighlightedFeatures } = props;

    const dispatch = useDispatch();

    const [features, setFeatures] = useState<Graphic[]>([]);
    const [selectedSource, setSelectedSource] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

    const viewLoaded = useSelector((state: StoreState) => state.app.viewLoaded);
    const catalogLoading = useSelector((state: StoreState) => state.search.catalogLoading);
    const activeVersionGuid = useSelector((state: StoreState) => state.version.activeGuid);
    const leftPanelActiveTab = useSelector((state: StoreState) => state.app.leftPanelActiveTab);

    const isCatalogActiveRef = useRef<boolean>(false);
    const featuresRef = useRef<Graphic[]>([]);

    useEffect(() => {
        featuresRef.current = features;
    }, [features]);

    const populateFeatureList = async (mapView: MapView, allLayers: Collection<Layer>) => {
        if (!isCatalogActiveRef.current) {
            return;
        }
        dispatch(setCatalogLoading(true));
        const allQuery = new Query();
        allQuery.geometry = mapView.extent;
        allQuery.spatialRelationship = "intersects";
        allQuery.outFields = ["*"];
        allQuery.returnGeometry = true;
        allQuery.geometryPrecision = 20;
        // allQuery.outSpatialReference = { wkid: 4326 };

        for (const layer of allLayers) {
            if (!layer.loaded) {
                await layer.load();
            }
            if (!layer.visible) {
                continue;
            }
            if ((layer as FeatureLayer).definitionExpression) {
                const nfsStatusField = (layer as FeatureLayer).fields.find(
                    (field) => field.name.toLowerCase() === "nfs_status"
                );
                if (nfsStatusField) {
                    allQuery.where = (layer as FeatureLayer).definitionExpression;
                } else {
                    allQuery.where = "1=1";
                }
            }
            if (layer.id === "IB") {
                // Exclude IB linked to FL/LA, they are handled through their main feature
                allQuery.where = `(${allQuery.where || "1=1"}) AND (${LayerFieldsIB.tilknyttet_fl_la} <> ${
                    JaNei.Ja
                } OR ${LayerFieldsIB.tilknyttet_fl_la} IS NULL)`;
            }
            const visibleFeatures = await (layer as FeatureLayer)?.queryFeatures(allQuery);
            const newFeatureArray = [...featuresRef.current, ...visibleFeatures.features];
            const uniqueFeatures = _.uniqWith(newFeatureArray, (a, b) => {
                return a.attributes.objectid === b.attributes.objectid;
            });
            setFeatures(uniqueFeatures);
            if (uniqueFeatures.length >= 100) {
                dispatch(setCatalogLoading(false));
                return;
            }
        }
        dispatch(setCatalogLoading(false));
    };

    const loadFeatures = async (allLayers: Collection<Layer>) => {
        const mapView = getMapView();
        if (!mapView) return;

        reactiveUtils.watch(
            () => mapView.navigating,
            async (navigating) => {
                if (navigating) {
                    setFeatures([]);
                } else {
                    populateFeatureList(mapView, allLayers);
                }
            }
        );
    };

    useEffect(() => {
        if (leftPanelActiveTab === LeftPanelTabs.CATALOG) {
            isCatalogActiveRef.current = true;
        } else {
            isCatalogActiveRef.current = false;
        }
    }, [leftPanelActiveTab]);

    useEffect(() => {
        if (leftPanelActiveTab !== LeftPanelTabs.CATALOG) {
            return;
        }
        const mapView = getMapView();
        if (!mapView?.map) return;

        const allLayerIds = AppConfig.Layers.filter((layer) => !layer.IsTable && layer.ExcludeFromSearch === false).map(
            (layer) => layer.Name
        );

        const allLayers = mapView.map.allLayers.filter((layer) => allLayerIds.includes(layer.id));

        setFeatures([]);
        loadFeatures(allLayers);
        populateFeatureList(mapView, allLayers);
    }, [viewLoaded, activeVersionGuid, leftPanelActiveTab]);

    return (
        <>
            <div className="search-filter">
                <ResultsFilter
                    results={features}
                    selectedSource={selectedSource}
                    setSelectedSource={setSelectedSource}
                    selectedStatus={selectedStatus}
                    setSelectedStatus={setSelectedStatus}
                    loading={catalogLoading}
                />
            </div>
            <div className="catalog-content">
                {features.length === 0 ? (
                    <span className="search-content-empty">Ingen resultater funnet.</span>
                ) : (
                    features.map((feature) => {
                        if (!selectedSource?.includes(feature.layer?.title ?? "") && selectedSource !== null) {
                            return null;
                        }

                        if (
                            GetCodedValueAndAlias(feature.layer, "nfs_status", feature.attributes["nfs_status"])[1] !==
                                selectedStatus &&
                            selectedStatus !== null
                        ) {
                            return null;
                        }

                        return (
                            <SearchItem
                                key={feature.attributes.objectid}
                                feature={feature}
                                storeHighlightHandle={storeHighlightHandle}
                                clearHighlightedFeatures={clearHighlightedFeatures}
                            />
                        );
                    })
                )}
            </div>
            {catalogLoading && (
                <div className="search-loader">
                    <Loader inline size={20} />
                </div>
            )}
        </>
    );
};

export default Catalog;
